import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { finalize } from 'rxjs';
import { Branch, BranchService } from '../../services/branch.service';

interface DropdownOption {
    label: string;
    value: string;
}

@Component({
    selector: 'platform-create-branch-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, DropdownModule, InputTextModule],
    templateUrl: './create-branch-dialog.component.html',
    styleUrls: ['./create-branch-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateBranchDialogComponent {
    private readonly branchService = inject(BranchService);
    private readonly destroyRef = inject(DestroyRef);

    /** Project ID for branch creation */
    readonly projectId = input.required<string>();

    /** Available branches for parent selection */
    readonly branches = input<Branch[]>([]);

    /** Dialog visibility (two-way binding) */
    readonly visible = model<boolean>(false);

    /** Emitted when branch is created (without checkout) */
    readonly branchCreated = output<Branch>();

    /** Emitted when branch is created and should be checked out */
    readonly branchCreatedAndCheckout = output<Branch>();

    /** Emitted on error */
    readonly error = output<string>();

    /** Branch name input value */
    branchName = '';

    /** Selected parent branch name */
    selectedParentBranch: string | null = null;

    /** Loading state during creation */
    readonly loading = signal(false);

    /** Dropdown options derived from branches */
    readonly parentBranchOptions = computed<DropdownOption[]>(() =>
        this.branches().map((b) => ({
            label: b.name,
            value: b.name,
        })),
    );

    /** Branch name validation error (real-time) */
    get branchNameError(): string {
        const name = this.branchName;
        if (!name) return '';

        if (!/^[a-zA-Z0-9\-_/]+$/.test(name)) {
            return 'Only letters, numbers, hyphens, underscores, and slashes allowed';
        }

        if (name.startsWith('/') || name.endsWith('/')) {
            return 'Cannot start or end with a slash';
        }

        if (name.includes('//')) {
            return 'Cannot contain consecutive slashes';
        }

        if (name.length > 255) {
            return 'Branch name too long (max 255 characters)';
        }

        return '';
    }

    /** Form validity */
    get isValid(): boolean {
        const name = this.branchName.trim();
        const parent = this.selectedParentBranch;
        const hasError = this.branchNameError;
        return name.length > 0 && parent !== null && !hasError;
    }

    /** Reset form state */
    reset(): void {
        this.branchName = '';
        this.selectedParentBranch = null;
        this.loading.set(false);
    }

    /** Handle dialog show */
    onShow(): void {
        // Pre-select 'main' or first branch if available
        const branches = this.branches();
        const mainBranch = branches.find((b) => b.name.toLowerCase() === 'main');
        if (mainBranch) {
            this.selectedParentBranch = mainBranch.name;
        } else if (branches.length > 0) {
            this.selectedParentBranch = branches[0].name;
        }
    }

    /** Handle dialog hide */
    onHide(): void {
        this.reset();
    }

    /** Cancel and close dialog */
    onCancel(): void {
        this.visible.set(false);
    }

    /** Create branch without checkout */
    onCreate(): void {
        this.createBranch(false);
    }

    /** Create branch and checkout */
    onCreateAndCheckout(): void {
        this.createBranch(true);
    }

    /** Internal create branch logic */
    private createBranch(checkout: boolean): void {
        if (!this.isValid || this.loading()) return;

        const parentBranchName = this.selectedParentBranch;
        const parentBranch = this.branches().find((b) => b.name === parentBranchName);

        if (!parentBranch) {
            this.error.emit('Parent branch not found');
            return;
        }

        this.loading.set(true);

        this.branchService
            .createBranch({
                projectId: this.projectId(),
                name: this.branchName.trim(),
                fromCommitId: parentBranch.headCommitId,
            })
            .pipe(
                finalize(() => this.loading.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (branch) => {
                    if (checkout) {
                        this.branchCreatedAndCheckout.emit(branch);
                    } else {
                        this.branchCreated.emit(branch);
                    }
                    this.visible.set(false);
                },
                error: (err) => {
                    const message = err?.error?.message || err?.message || 'Failed to create branch';
                    this.error.emit(message);
                },
            });
    }
}
